import { z } from "zod";
import {
  Duration,
  Effort,
  ExperienceLevel,
  JobCategoryEnum,
  PaymentType,
  RemotePolicy,
  StartDate,
} from "../../create/constants";

export const descriptionEditSchema = z.object({
  description: z.string().min(1),
});

export type DescriptionEditValues = z.infer<typeof descriptionEditSchema>;

/** The three states of an offer's subcategories (`OfferSubcategories`). */
export const offerSubcategoriesSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("undeclared") }),
  z.object({ kind: z.literal("whole") }),
  z.object({ kind: z.literal("chosen"), keys: z.array(z.string()) }),
]);

export const titleCategoryEditSchema = z.object({
  title: z.string().min(1),
  job_category: z.nativeEnum(JobCategoryEnum).nullable(),
  subcategories: offerSubcategoriesSchema,
});

export type TitleCategoryEditValues = z.infer<typeof titleCategoryEditSchema>;

export const detailsEditSchema = z.object({
  payment_type: z.nativeEnum(PaymentType).nullable(),
  remote_policy: z.nativeEnum(RemotePolicy).nullable(),
  duration: z.nativeEnum(Duration).nullable(),
  experience_level: z.nativeEnum(ExperienceLevel).nullable(),
  effort: z.nativeEnum(Effort).nullable(),
  start_date: z.nativeEnum(StartDate).nullable(),
  budget: z.number().nullable(),
  daily_rate_min: z.number().nullable(),
  daily_rate_max: z.number().nullable(),
  location: z.string(),
  renewable: z.boolean(),
});

export type DetailsEditValues = z.infer<typeof detailsEditSchema>;

type ValidationSuccess = { success: true };
type ValidationFailure = {
  success: false;
  errorsByStep: Record<number, string[]>;
};

export function validateRefineState(state: {
  title: string;
  job_category: string | null;
  description: string;
  remote_policy: string | null;
  payment_type: string | null;
  internal_ats: boolean;
  apply_url: string;
}): ValidationSuccess | ValidationFailure {
  const errorsByStep: Record<number, string[]> = {};

  // Step 1: Title & Category
  const step1Errors: string[] = [];
  if (!state.title || state.title.trim() === "") {
    step1Errors.push("Il titolo è obbligatorio");
  }
  if (!state.job_category) {
    step1Errors.push("La categoria è obbligatoria");
  }
  if (step1Errors.length > 0) errorsByStep[1] = step1Errors;

  // Step 2: Details
  const step2Errors: string[] = [];
  if (!state.remote_policy) {
    step2Errors.push("La modalità di lavoro è obbligatoria");
  }
  if (!state.payment_type) {
    step2Errors.push("Il tipo di pagamento è obbligatorio");
  }
  if (step2Errors.length > 0) errorsByStep[2] = step2Errors;

  // Step 4: Description
  if (!state.description || state.description.trim() === "") {
    errorsByStep[4] = ["La descrizione è obbligatoria"];
  }

  // Step 5: Application
  if (
    !state.internal_ats &&
    (!state.apply_url || state.apply_url.trim() === "")
  ) {
    errorsByStep[5] = ["L'URL di candidatura è obbligatorio per ATS custom"];
  }

  if (Object.keys(errorsByStep).length > 0) {
    return { success: false, errorsByStep };
  }
  return { success: true };
}
